'use client';

import type { Tile } from './WordleGrid';
import { KEYBOARD_LAYOUTS, type KeyboardLayout } from '@/utils/keyboardLayouts';

type KeyState = 'default' | 'correct' | 'present' | 'absent';

interface WordleKeyboardProps {
  grid: Tile[][];
  currentRow: number;
  pressedKey: string | null;
  onKeyPress: (key: string) => void;
  layout: KeyboardLayout;
}

export function WordleKeyboard({
  grid,
  currentRow,
  pressedKey,
  onKeyPress,
  layout,
}: WordleKeyboardProps) {
  const letterStates: Record<string, KeyState> = {};

  grid.slice(0, currentRow).forEach(row => {
    row.forEach(tile => {
      if (!tile.letter) return;
      const prev = letterStates[tile.letter];
      if (prev === 'correct') return;
      if (prev === 'present' && tile.state === 'absent') return;
      letterStates[tile.letter] = tile.state;
    });
  });

  const rows = KEYBOARD_LAYOUTS[layout];

  const getKeyClasses = (state: KeyState) => {
    switch (state) {
      case 'correct':
        return 'bg-[var(--color-game-letter-correct)] text-white';
      case 'present':
        return 'bg-[var(--color-game-letter-present)] text-white';
      case 'absent':
        return 'bg-[var(--color-game-letter-absent)] text-white opacity-70';
      default:
        return 'bg-neutral-600 text-white';
    }
  };

  return (
    <div className="mx-auto w-full max-w-[470px] select-none px-3 sm:pb-2 pb-5">
      <div className="flex flex-col gap-1">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-1">
            {row.map(key => {
              const isLarge = key === 'ENTER' || key === 'BACKSPACE';
              const state = letterStates[key] ?? 'default';
              const isPressed = pressedKey === key;

              return (
                <button
                  key={key}
                  type="button"
                  onPointerUp={() => onKeyPress(key)}
                  className={`
                    ${getKeyClasses(state)}
                    ${isLarge ? 'flex-[1.5]' : 'flex-1'}
                    h-13 sm:h-14
                    rounded-md
                    font-bold
                    flex items-center justify-center
                    transition-all
                    ${isPressed ? 'scale-95 brightness-125' : ''}
                  `}
                >
                  {key === 'BACKSPACE' ? '⌫' : key}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
